import React, { createContext, useContext, useState } from 'react'
import { Customer, User, WorkOrder, AuthState } from './types'
import { mockCustomers, mockUsers, mockWorkOrders } from './mock-data'
import { toast } from 'sonner'

interface AppContextType {
  auth: AuthState
  login: (provider: 'Microsoft' | 'Google') => void
  logout: () => void

  customers: Customer[]
  addCustomer: (
    customer: Omit<Customer, 'id' | 'created_at' | 'updated_at'>,
  ) => void
  updateCustomer: (id: string, customer: Partial<Customer>) => void
  deleteCustomer: (id: string) => void

  users: User[]
  updateUser: (id: string, user: Partial<User>) => void

  workOrders: WorkOrder[]
  addWorkOrder: (
    wo: Omit<
      WorkOrder,
      | 'id'
      | 'created_at'
      | 'updated_at'
      | 'work_order_number'
      | 'sequence_number'
      | 'year'
    >,
  ) => void
  updateWorkOrder: (id: string, wo: Partial<WorkOrder>) => void
  deleteWorkOrder: (id: string) => void
  duplicateWorkOrder: (id: string) => void

  generateWorkOrderNumber: (
    customerId: string,
    year: number,
  ) => { number: string; sequence: number }
}

const AppContext = createContext<AppContextType | undefined>(undefined)

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [auth, setAuth] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
  })
  const [customers, setCustomers] = useState<Customer[]>(mockCustomers)
  const [users, setUsers] = useState<User[]>(mockUsers)
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>(mockWorkOrders)

  const login = (provider: 'Microsoft' | 'Google') => {
    setAuth({ isAuthenticated: true, user: users[0] })
    toast.success(`Uspešna prijava z ${provider}`)
  }

  const logout = () => {
    setAuth({ isAuthenticated: false, user: null })
    toast.info('Odjavljeni ste bili')
  }

  const addCustomer = (
    customer: Omit<Customer, 'id' | 'created_at' | 'updated_at'>,
  ) => {
    const newCustomer: Customer = {
      ...customer,
      id: `c${Date.now()}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    setCustomers((prev) => [...prev, newCustomer])
    toast.success('Stranka uspešno dodana')
  }

  const updateCustomer = (id: string, data: Partial<Customer>) => {
    setCustomers((prev) =>
      prev.map((c) =>
        c.id === id
          ? { ...c, ...data, updated_at: new Date().toISOString() }
          : c,
      ),
    )
    toast.success('Stranka uspešno posodobljena')
  }

  const deleteCustomer = (id: string) => {
    setCustomers((prev) => prev.filter((c) => c.id !== id))
    toast.success('Stranka izbrisana')
  }

  const updateUser = (id: string, data: Partial<User>) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === id
          ? { ...u, ...data, updated_at: new Date().toISOString() }
          : u,
      ),
    )
    toast.success('Uporabnik posodobljen')
  }

  const generateWorkOrderNumber = (customerId: string, year: number) => {
    const customer = customers.find((c) => c.id === customerId)
    if (!customer) return { number: '', sequence: 0 }

    const customerWos = workOrders.filter(
      (wo) => wo.customer_id === customerId && wo.year === year,
    )
    const startSeq = customer.sequence_start || 1
    const maxSeq =
      customerWos.length > 0
        ? Math.max(...customerWos.map((wo) => wo.sequence_number))
        : startSeq - 1
    const nextSeq = maxSeq + 1

    const yearStr = year.toString().slice(-2)
    const seqStr = nextSeq.toString().padStart(3, '0')

    return {
      number: `${customer.prefix}-${yearStr}-${seqStr}`,
      sequence: nextSeq,
    }
  }

  const addWorkOrder = (
    wo: Omit<
      WorkOrder,
      | 'id'
      | 'created_at'
      | 'updated_at'
      | 'work_order_number'
      | 'sequence_number'
      | 'year'
    >,
  ) => {
    const currentYear = new Date().getFullYear()
    const { number, sequence } = generateWorkOrderNumber(
      wo.customer_id,
      currentYear,
    )

    const newWo: WorkOrder = {
      ...wo,
      id: `wo${Date.now()}`,
      work_order_number: number,
      sequence_number: sequence,
      year: currentYear,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    setWorkOrders((prev) => [newWo, ...prev])
    toast.success('Delovni nalog uspešno ustvarjen')
  }

  const updateWorkOrder = (id: string, data: Partial<WorkOrder>) => {
    setWorkOrders((prev) =>
      prev.map((wo) =>
        wo.id === id
          ? { ...wo, ...data, updated_at: new Date().toISOString() }
          : wo,
      ),
    )
    toast.success('Delovni nalog uspešno posodobljen')
  }

  const deleteWorkOrder = (id: string) => {
    setWorkOrders((prev) => prev.filter((wo) => wo.id !== id))
    toast.success('Delovni nalog izbrisan')
  }

  const duplicateWorkOrder = (id: string) => {
    const woToDuplicate = workOrders.find((wo) => wo.id === id)
    if (!woToDuplicate) return

    const currentYear = new Date().getFullYear()
    const { number, sequence } = generateWorkOrderNumber(
      woToDuplicate.customer_id,
      currentYear,
    )

    const newWo: WorkOrder = {
      ...woToDuplicate,
      id: `wo${Date.now()}`,
      work_order_number: number,
      sequence_number: sequence,
      year: currentYear,
      status: 'Osnutek',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    setWorkOrders((prev) => [newWo, ...prev])
    toast.success('Delovni nalog podvojen')
  }

  return React.createElement(
    AppContext.Provider,
    {
      value: {
        auth,
        login,
        logout,
        customers,
        addCustomer,
        updateCustomer,
        deleteCustomer,
        users,
        updateUser,
        workOrders,
        addWorkOrder,
        updateWorkOrder,
        deleteWorkOrder,
        duplicateWorkOrder,
        generateWorkOrderNumber,
      },
    },
    children,
  )
}

export const useAppContext = () => {
  const context = useContext(AppContext)
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider')
  }
  return context
}
